/**
 * Synthesis Layer API service
 * All API calls for the demand intelligence synthesis dashboard
 */

import { authFetch, handleResponse } from '@/utils/auth-fetch';

const BASE = '/api/v1/synthesis';

// ── Types ────────────────────────────────────────────────────────────────

export interface Market {
  market: string;
  store: string;
  language: string;
  total_gap_score: number;
  total_eur_value: number;
  cluster_count: number;
  trending: boolean;
}

export interface Cluster {
  id: string;
  site_id: string;
  market: string;
  language: string;
  store: string;
  label: string;
  member_count: number;
  top_queries: { query: string; value: number; source: string }[];
  total_commercial_value_eur: number;
  coverage_pct: number;
  competitor_cover_rate: number;
  gap_score: number;
  trending: boolean;
  strategic_weight: number;
  depth: number;
  parent_cluster_id: string | null;
  pinned: boolean;
  volume_14d: number;
  volume_90d_baseline: number;
  // Galaxy-specific (pre-computed)
  umap_x?: number;
  umap_y?: number;
}

export interface GapQueueItem {
  id: string;
  site_id: string;
  cluster_id: string;
  cluster_label: string;
  gap_score: number;
  total_commercial_value_eur: number;
  coverage_pct: number;
  competitor_cover_rate: number;
  dominant_authority: string;
  suggested_action: string;
  top_unanswered_queries: { query: string; value: number; source: string }[];
  status: string;
  assigned_to: string | null;
}

export interface VocabularyGap {
  id: string;
  store: string;
  language: string;
  onsite_term: string;
  google_term: string;
  cosine_similarity: number;
  onsite_search_count: number;
  google_monthly_volume: number;
  google_cpc_eur: number;
  is_synonym_candidate: boolean;
  synonym_approved: boolean;
}

export interface PaidOrganicOverlap {
  id: string;
  query: string;
  gsc_position: number;
  gsc_impressions_30d: number;
  gsc_clicks_30d: number;
  ads_avg_cpc_eur: number;
  ads_monthly_impressions: number;
  estimated_monthly_spend_eur: number;
  reclaimable_eur_per_month: number;
  overlap_type: string;
}

export interface ContentRoiItem {
  id: string;
  page_id: string;
  aeo_generation_id: string;
  deploy_date: string;
  keyword_count: number;
  avg_position_before: number;
  avg_position_after: number;
  position_delta: number;
  traffic_value_before_eur: number;
  traffic_value_after_eur: number;
  traffic_value_delta_eur: number;
  confidence: string;
}

export interface ChatbotSelfHealItem {
  id: string;
  chatbot_gap_id: string;
  question_text: string;
  frequency_count: number;
  avg_quality_score: number;
  cluster_id: string;
  cluster_label: string;
  authority: string;
  draft_status: string;
}

export interface CorpusStats {
  total: number;
  totalEmbedded: number;
  embeddingPct: number;
  sources: { source_type: string; count: string; embedded_count: string; total_commercial_value: string }[];
}

// ── API Functions ────────────────────────────────────────────────────────

export async function fetchMarkets(siteId: string): Promise<Market[]> {
  const resp = await authFetch(`${BASE}/clusters?siteId=${siteId}&limit=500`);
  const data = await handleResponse<{ clusters: Cluster[] }>(resp);

  // Aggregate clusters into markets
  const num = (v: unknown): number => { const n = Number(v); return isNaN(n) ? 0 : n; };
  const marketMap = new Map<string, Market>();
  for (const c of data.clusters) {
    const existing = marketMap.get(c.market);
    if (existing) {
      existing.total_gap_score += num(c.gap_score);
      existing.total_eur_value += num(c.total_commercial_value_eur);
      existing.cluster_count++;
      if (c.trending) existing.trending = true;
    } else {
      marketMap.set(c.market, {
        market: c.market,
        store: c.store,
        language: c.language,
        total_gap_score: num(c.gap_score),
        total_eur_value: num(c.total_commercial_value_eur),
        cluster_count: 1,
        trending: c.trending || false,
      });
    }
  }
  return Array.from(marketMap.values()).sort((a, b) => b.total_gap_score - a.total_gap_score);
}

export async function fetchClusters(siteId: string, market?: string): Promise<Cluster[]> {
  let url = `${BASE}/clusters?siteId=${siteId}&limit=200`;
  if (market) url += `&market=${encodeURIComponent(market)}`;
  const resp = await authFetch(url);
  const data = await handleResponse<{ clusters: Cluster[] }>(resp);
  return data.clusters;
}

export async function fetchGapQueue(siteId: string, options: {
  market?: string; status?: string; sort?: string; authority?: string; limit?: number;
} = {}): Promise<GapQueueItem[]> {
  const params = new URLSearchParams({ siteId });
  if (options.market) params.set('market', options.market);
  if (options.status) params.set('status', options.status);
  if (options.authority) params.set('authority', options.authority);
  if (options.limit) params.set('limit', String(options.limit));
  const resp = await authFetch(`${BASE}/gap-queue?${params}`);
  const data = await handleResponse<{ gaps: GapQueueItem[] }>(resp);
  return data.gaps;
}

export async function updateGapStatus(id: string, update: { status?: string; assigned_to?: string }): Promise<void> {
  await authFetch(`${BASE}/gap-queue/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(update),
  });
}

export async function fetchVocabularyGaps(siteId: string, options: {
  store?: string; language?: string; synonymOnly?: boolean;
} = {}): Promise<VocabularyGap[]> {
  const params = new URLSearchParams({ siteId });
  if (options.store) params.set('store', options.store);
  if (options.language) params.set('language', options.language);
  if (options.synonymOnly) params.set('synonymOnly', 'true');
  const resp = await authFetch(`${BASE}/vocabulary-gaps?${params}`);
  const data = await handleResponse<{ gaps: VocabularyGap[] }>(resp);
  return data.gaps;
}

export async function approveVocabularyGap(id: string): Promise<void> {
  await authFetch(`${BASE}/vocabulary-gaps/${id}/approve`, { method: 'PATCH' });
}

export async function fetchPaidOrganicOverlap(siteId: string): Promise<{
  overlaps: PaidOrganicOverlap[]; totalReclaimable: number;
}> {
  const resp = await authFetch(`${BASE}/paid-organic-overlap?siteId=${siteId}`);
  return handleResponse(resp);
}

export async function fetchRoiSummary(siteId: string, days = 90): Promise<{
  summary: { pagesDeployed: number; totalValueDeltaEur: number; avgPositionDelta: number; pagesWithPositiveRoi: number };
  pages: ContentRoiItem[];
}> {
  const resp = await authFetch(`${BASE}/roi-summary?siteId=${siteId}&days=${days}`);
  return handleResponse(resp);
}

export async function fetchCorpusStats(siteId: string): Promise<CorpusStats> {
  const resp = await authFetch(`${BASE}/corpus/stats/${siteId}`);
  return handleResponse(resp);
}

export async function triggerPipeline(siteId: string): Promise<void> {
  await authFetch(`${BASE}/run/${siteId}`, { method: 'POST' });
}
